import React from 'react';
import { Box, FieldTypes, Inscription } from '@common-fe/common-fe';

import { EmployerReactivationAccountUsageField } from '@/modules/employer/components/EmployerReactivation/EmployerReactivationAccountUsage';
import { useEmployerReactivationBankListData } from '@/modules/employer/components/EmployerReactivation/EmployerReactivationBankList/hooks/useEmployerReactivationBankListData';
import { IBillingMethodTypePropertyDtoBillingMethodTypeEnum } from '@/types';

import { useEmployerReactivationCardAndPlans } from './hooks/useEmployerReactivationCardAndPlans';
import { EmployerReactivationPlansList } from './EmployerReactivationPlansList';

const RE_CARD_DESCRIPTION = `Re-carding employees will send new cards for all employees once enrolled in a carded plan.
 Their current cards will no longer work. If plans are not re-carded, employees can continue to use their existing cards.`;

const PLANS_DESCRIPTION = `Plans will be reactivated using all prior settings. Settings can be modified after activation by editing the plan.
 Only plans with remaining dates of coverage can be reactivated.`;

const NO_PLANS_DESCRIPTION = 'This employer doesn`t have plans eligible for reactivation';

interface Props {
  hasFieldErrors?: boolean;
}

export const EmployerReactivationCardAndPlans: React.FC<Props> = ({ hasFieldErrors }) => {
  const { options } = useEmployerReactivationBankListData();
  const {
    reCardEmployees,
    reactivatePlans,
    hasBankAccounts,
    plans,
    handleReCardChange,
    handleReactivatePlansChange,
  } = useEmployerReactivationCardAndPlans();

  return (
    <Box round="container1Round" pad="spacing24" background="module" margin={{ top: 'spacing24' }}>
      {!hasBankAccounts ? (
        <Box
          pad="spacing24"
          round="container1Round"
          background={{ color: 'canvas' }}
          border={{ size: '1px', color: 'border2' }}
          data-testid="EmployerReactivationCardAndPlans_placeholder"
        >
          <Inscription>
            Plan and card reactivation options can be selected once a bank account is added.
          </Inscription>
        </Box>
      ) : (
        <Box
          pad="spacing24"
          round="container1Round"
          background={{ color: 'canvas' }}
          border={{ size: '1px', color: 'border2' }}
          data-testid="EmployerReactivationCardAndPlans_form"
        >
          <EmployerReactivationAccountUsageField
            title='Re-card all employees'
            description={RE_CARD_DESCRIPTION}
            fieldType={FieldTypes.Checkbox}
            methodType={IBillingMethodTypePropertyDtoBillingMethodTypeEnum.PULL_FUNDS}
            testId='EmployerReactivationCardAndPlans_re_card_employees'
            isRequired={false}
            checked={reCardEmployees}
            onChange={handleReCardChange}
          />

          <Box
            width="100%"
            height="1px"
            background={{ color: 'border1' }}
            margin={{ vertical: 'spacing16'}}
          />

          <EmployerReactivationAccountUsageField
            title='Reactivate plans'
            description={PLANS_DESCRIPTION}
            fieldType={FieldTypes.Checkbox}
            methodType={IBillingMethodTypePropertyDtoBillingMethodTypeEnum.PULL_FUNDS}
            testId='EmployerReactivationCardAndPlans_reactivate_plans'
            isRequired={false}
            checked={reactivatePlans}
            onChange={handleReactivatePlansChange}
            disabled={plans.length === 0}
            disabledText={plans.length === 0 ? NO_PLANS_DESCRIPTION : undefined}
          />

          {reactivatePlans && <EmployerReactivationPlansList plans={plans} options={options} hasFieldErrors={hasFieldErrors} />}
        </Box>
      )}
    </Box>
  );
};
